import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/** Split a free-text scope list on commas/whitespace. */
export function splitScopes(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface ScopeSuggestion {
  value: string;
  /** Shown under the suggestion row when this one is offered. */
  hint?: string;
}

export interface ScopeChipsInputProps {
  /** Current scopes, in order. Controlled — always renders exactly this. */
  value: string[];
  /** Next list on every add/remove. Never called with blanks or duplicates. */
  onChange: (next: string[]) => void;
  label?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** One-click additions rendered under the field; already-added ones are hidden. */
  suggestions?: ScopeSuggestion[];
}

export interface ScopeChipsInputHandle {
  /**
   * Commit a half-typed scope. Call before submitting: a user who types a
   * scope and clicks Save without pressing Enter would otherwise lose it,
   * and blur ordering is not reliable across browsers.
   */
  flush: () => void;
}

/**
 * Multi-value input for OAuth scopes.
 *
 * Replaces a plain comma-separated text field — which gave no hint that it
 * took a list, and made removing one scope a string-editing exercise. Scopes
 * are added with Enter/comma/space/Tab/blur or by pasting a list (splitting
 * the same way), and removed individually.
 */
export const ScopeChipsInput = React.forwardRef<ScopeChipsInputHandle, ScopeChipsInputProps>(
  (
    {
      value,
      onChange,
      label,
      hint,
      error,
      placeholder,
      disabled,
      id,
      className,
      suggestions,
    },
    ref
  ) => {
    const [draft, setDraft] = React.useState("");
    const [duplicate, setDuplicate] = React.useState<string | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const inputId = id || label?.toLowerCase().replace(/\s+/g, "-");

    // Flash the existing chip rather than erroring: re-adding a scope is a
    // slip, not a mistake worth blocking on.
    React.useEffect(() => {
      if (!duplicate) return;
      const t = setTimeout(() => setDuplicate(null), 700);
      return () => clearTimeout(t);
    }, [duplicate]);

    const addAll = React.useCallback(
      (candidates: string[]) => {
        const next = [...value];
        let addedAny = false;
        for (const candidate of candidates) {
          if (next.includes(candidate)) {
            setDuplicate(candidate);
            continue;
          }
          next.push(candidate);
          addedAny = true;
        }
        if (addedAny) onChange(next);
      },
      [value, onChange]
    );

    const commitDraft = React.useCallback(() => {
      const candidates = splitScopes(draft);
      setDraft("");
      if (candidates.length > 0) addAll(candidates);
    }, [draft, addAll]);

    React.useImperativeHandle(ref, () => ({ flush: commitDraft }), [commitDraft]);

    const remove = (scope: string) => onChange(value.filter((s) => s !== scope));

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        // Enter must never reach the enclosing <form> — both call sites are
        // inside one, and submitting mid-entry loses the scope being typed.
        e.preventDefault();
        commitDraft();
        return;
      }
      if (e.key === " " && draft.trim().length > 0) {
        e.preventDefault();
        commitDraft();
        return;
      }
      if (e.key === "Tab" && draft.trim().length > 0) {
        commitDraft();
        return;
      }
      if (e.key === "Backspace" && draft.length === 0 && value.length > 0) {
        remove(value[value.length - 1]);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData("text");
      if (!/[,\s]/.test(text)) return; // single token — let it type normally
      e.preventDefault();
      setDraft("");
      addAll(splitScopes(text));
    };

    const unusedSuggestions = (suggestions ?? []).filter((s) => !value.includes(s.value));

    const control = (
      <div
        className={cn(
          "focus-ring flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 text-sm ring-offset-background",
          error && "border-red-300 focus-visible:ring-red-500",
          disabled && "cursor-not-allowed opacity-50",
          className
        )}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
      >
        <ul role="list" className="contents">
          {value.map((scope) => (
            <li
              key={scope}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground",
                duplicate === scope && "ring-1 ring-primary-400"
              )}
            >
              <span className="max-w-[18rem] truncate" title={scope}>
                {scope}
              </span>
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove scope ${scope}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(scope);
                  }}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground hover:bg-background hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={draft}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={commitDraft}
          className="h-6 min-w-[8rem] flex-1 bg-transparent px-1 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
        <span className="sr-only" aria-live="polite">
          {value.length} {value.length === 1 ? "scope" : "scopes"}
        </span>
      </div>
    );

    const suggestionRow = unusedSuggestions.length > 0 && !disabled && (
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Suggested:</span>
        {unusedSuggestions.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => addAll([s.value])}
            title={s.hint}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:border-solid hover:text-foreground"
          >
            + {s.value}
          </button>
        ))}
      </div>
    );

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-foreground mb-1"
          >
            {label}
          </label>
        )}
        {control}
        {suggestionRow}
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        {hint && !error && (
          <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
        )}
        {unusedSuggestions.some((s) => s.hint) && !error && (
          <p className="mt-1 text-xs text-muted-foreground">
            {unusedSuggestions.find((s) => s.hint)?.hint}
          </p>
        )}
      </div>
    );
  }
);
ScopeChipsInput.displayName = "ScopeChipsInput";

export default ScopeChipsInput;
